/**
 * Quick order list (Dutton Brown)
 *
 * Drives sections/quick-order-list.liquid. Vanilla JS, no dependencies.
 *
 * - Every table row with a quantity box is a line; the sticky bar shows the line count and subtotal.
 * - Quantities are coerced to the variant's quantity rule (min, max, increment) on change.
 * - The subtotal honors quantity price breaks (data-breaks="min:price,min:price", prices in cents).
 * - The SKU box matches a row by SKU, adds the typed quantity to it and scrolls it into view.
 * - "Add to cart" posts every non-zero line in one /cart/add.js call, then opens the cart drawer
 *   (Impact listens for cart:change with baseEvent "variant:add") or goes to /cart.
 */
(function () {
  'use strict';

  if (window.customElements.get('quick-order-list')) {
    return;
  }

  function formatMoney(cents) {
    var settings = (window.themeVariables && window.themeVariables.settings) || {};
    var format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    format = format || '${{amount}}';

    var amount = Number(cents) / 100;
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    var key = match ? match[1] : 'amount';
    var value;

    switch (key) {
      case 'amount_no_decimals':
        value = Math.round(amount).toLocaleString('en-US');
        break;
      case 'amount_with_comma_separator':
        value = amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        break;
      default:
        value = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function parseBreaks(raw) {
    if (!raw) {
      return [];
    }

    return raw.split(',').map(function (pair) {
      var parts = pair.split(':');
      return { min: parseInt(parts[0], 10), price: parseInt(parts[1], 10) };
    }).filter(function (entry) {
      return !isNaN(entry.min) && !isNaN(entry.price);
    }).sort(function (a, b) {
      return a.min - b.min;
    });
  }

  function unitPrice(row, quantity) {
    var price = parseInt(row.dataset.price, 10) || 0;
    var breaks = parseBreaks(row.dataset.breaks);

    for (var i = 0; i < breaks.length; i++) {
      if (quantity >= breaks[i].min) {
        price = breaks[i].price;
      }
    }

    return price;
  }

  /** Snap a requested quantity to the row's quantity rule. 0 always means "no line". */
  function coerceQuantity(input, requested) {
    var min = parseInt(input.dataset.min, 10) || 1;
    var increment = parseInt(input.dataset.increment, 10) || 1;
    var max = parseInt(input.dataset.max, 10);
    var quantity = parseInt(requested, 10);

    if (isNaN(quantity) || quantity <= 0) {
      return 0;
    }

    if (quantity < min) {
      quantity = min;
    }

    var over = (quantity - min) % increment;

    if (over !== 0) {
      quantity += increment - over;
    }

    if (!isNaN(max) && quantity > max) {
      quantity = max - ((max - min) % increment);
    }

    return quantity;
  }

  class QuickOrderList extends HTMLElement {
    connectedCallback() {
      this.rows = Array.prototype.slice.call(this.querySelectorAll('[data-row]'));
      this.lineCount = this.querySelector('[data-line-count]');
      this.subtotal = this.querySelector('[data-subtotal]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.submitMessage = this.querySelector('[data-submit-message]');
      this.skuForm = this.querySelector('[data-sku-form]');
      this.skuInput = this.querySelector('[data-sku-input]');
      this.skuQty = this.querySelector('[data-sku-qty]');
      this.skuMessage = this.querySelector('[data-sku-message]');

      this.addEventListener('change', this.onQuantityChange.bind(this));
      this.addEventListener('input', this.onQuantityInput.bind(this));

      if (this.skuForm) {
        this.skuForm.addEventListener('submit', this.onSkuSubmit.bind(this));
      }

      if (this.addButton) {
        this.addButton.addEventListener('click', this.addToCart.bind(this));
      }

      this.updateTotals();
    }

    lines() {
      var lines = [];

      this.rows.forEach(function (row) {
        var input = row.querySelector('[data-qty]');

        if (!input || input.disabled) {
          return;
        }

        var quantity = parseInt(input.value, 10);

        if (quantity > 0) {
          lines.push({ row: row, input: input, quantity: quantity });
        }
      });

      return lines;
    }

    onQuantityInput(event) {
      if (event.target.matches('[data-qty]')) {
        this.updateTotals();
      }
    }

    onQuantityChange(event) {
      var input = event.target;

      if (!input.matches('[data-qty]')) {
        return;
      }

      var coerced = coerceQuantity(input, input.value);
      input.value = coerced === 0 ? '' : coerced;
      this.updateTotals();
    }

    updateTotals() {
      var lines = this.lines();
      var total = 0;

      lines.forEach(function (line) {
        total += unitPrice(line.row, line.quantity) * line.quantity;
      });

      if (this.lineCount) {
        this.lineCount.textContent = lines.length + (lines.length === 1 ? ' line' : ' lines');
      }

      if (this.subtotal) {
        this.subtotal.textContent = formatMoney(total);
      }

      if (this.addButton) {
        this.addButton.disabled = lines.length === 0;
      }
    }

    findRowBySku(sku) {
      var wanted = sku.trim().toLowerCase();

      if (!wanted) {
        return null;
      }

      for (var i = 0; i < this.rows.length; i++) {
        if ((this.rows[i].dataset.sku || '').trim().toLowerCase() === wanted) {
          return this.rows[i];
        }
      }

      return null;
    }

    onSkuSubmit(event) {
      event.preventDefault();

      var sku = this.skuInput ? this.skuInput.value : '';
      var row = this.findRowBySku(sku);

      if (!row) {
        this.say(this.skuMessage, 'No SKU "' + sku.trim() + '" in this list.');
        return;
      }

      var input = row.querySelector('[data-qty]');

      if (!input || input.disabled) {
        this.say(this.skuMessage, sku.trim() + ' is unavailable right now.');
        return;
      }

      var current = parseInt(input.value, 10) || 0;
      var add = parseInt(this.skuQty ? this.skuQty.value : 1, 10) || 1;
      var next = coerceQuantity(input, current + add);

      input.value = next;
      this.updateTotals();
      this.highlight(row);
      this.say(this.skuMessage, 'Added ' + sku.trim() + ', now ' + next + '.');

      this.skuInput.value = '';
      if (this.skuQty) {
        this.skuQty.value = 1;
      }
      this.skuInput.focus();
    }

    highlight(row) {
      this.rows.forEach(function (other) {
        other.classList.remove('is-highlighted');
      });
      row.classList.add('is-highlighted');
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    say(target, text) {
      if (target) {
        target.textContent = text;
      }
    }

    async addToCart() {
      var lines = this.lines();

      if (lines.length === 0) {
        return;
      }

      var items = lines.map(function (line) {
        return { id: parseInt(line.row.dataset.variantId, 10), quantity: line.quantity };
      });

      this.say(this.submitMessage, '');
      this.addButton.setAttribute('aria-busy', 'true');

      try {
        var response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        });

        var json = await response.json();

        if (!response.ok) {
          this.say(this.submitMessage, json.description || json.message || 'Could not add these lines.');
          return;
        }

        var cartType = window.themeVariables && window.themeVariables.settings.cartType;

        if (cartType !== 'drawer' && cartType !== 'popover') {
          window.location.href = this.dataset.cartUrl || (window.Shopify.routes.root + 'cart');
          return;
        }

        var cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();

        lines.forEach(function (line) {
          line.input.value = '';
        });
        this.updateTotals();

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cart }
        }));

        this.say(this.submitMessage, items.length + (items.length === 1 ? ' line added.' : ' lines added.'));
      } catch (error) {
        this.say(this.submitMessage, 'Could not reach the cart. Try again.');
      } finally {
        this.addButton.removeAttribute('aria-busy');
      }
    }
  }

  window.customElements.define('quick-order-list', QuickOrderList);
})();
